/**
 * One refinement session, and — PD-4 — the way back into the split plane.
 *
 * A session is the thing a `questions_ready` result node announces, per side.
 * Until this package the card was a dead end: an operator looking at
 * "questions_ready_both, attempt 2, 0.55" had no way to reach the node that
 * said so, or the conversation the questions were asked in. Both directions are
 * PD-4's acceptance (critic b: "the reverse direction requires editing those
 * three files"), so the links live here rather than being inferred by a reader.
 *
 * The node link filters rather than addresses, and that is forced: a result
 * key is `{requestKey}__questions_ready` on a request-ful node and
 * `{pebbleId}__partner_request_{originId}__questions_ready` on the side the
 * PARTNER's submit caused, and neither is derivable from a session row — it
 * carries no story version and no origin id. So the link is
 * `/match-events/{pebbleId}?kind=questions_ready`, which opens that pebble's
 * log showing exactly the questions_ready nodes. Addressing a key this card
 * cannot know would be a link that lands nowhere.
 */
import { Link } from 'react-router-dom';
import type { RefinementSession } from '../../api/types';
import { conversationPath, matchEventsPath } from '../matchevents/model';

interface Props {
  session: RefinementSession;
}

export default function RefinementSessionCard({ session }: Props) {
  const tone = statusTone(session.status);
  return (
    <div className="card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs text-gray-500">Refinement session</div>
          <code className="text-sm text-gray-800">{session.id}</code>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded text-sm font-medium ${tone}`}>
          {session.status}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-xs text-gray-500">Attempt</div>
          <div className="font-semibold">
            {session.attempt}/{session.maxAttempts}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Last score</div>
          <div className="font-semibold">
            {session.lastScore != null ? session.lastScore.toFixed(3) : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Updated</div>
          <div className="font-semibold">
            {new Date(session.updatedAt).toLocaleString()}
          </div>
        </div>
      </div>
      <SideLinks side="A" pebbleId={session.pebbleAId} />
      <SideLinks side="B" pebbleId={session.pebbleBId} />
    </div>
  );
}

/** One side's way out: the conversation this session's questions belong to
 *  (PD-2) and the `/match_events` log where its `questions_ready` node lives
 *  (PD-4). Rendered only for a side that HAS a pebble — a pre-split session
 *  carries none, and a link to `/conversation/null` is worse than no link. */
function SideLinks({ side, pebbleId }: { side: 'A' | 'B'; pebbleId: string | null }) {
  if (!pebbleId) return null;
  return (
    <div className="mt-2 flex items-center gap-3 text-xs" data-testid={`session-links-${side.toLowerCase()}`}>
      <span className="text-gray-500">Side {side}</span>
      <Link to={conversationPath(pebbleId)} className="text-pebbl-600 hover:text-pebbl-800">
        timeline →
      </Link>
      <Link
        to={matchEventsPath(pebbleId, { kind: 'questions_ready' })}
        className="text-pebbl-600 hover:text-pebbl-800"
      >
        questions_ready node →
      </Link>
      <code className="text-gray-400">{pebbleId}</code>
    </div>
  );
}

function statusTone(status: string): string {
  if (status === 'escalated_consent') return 'bg-green-100 text-green-800';
  if (status === 'failed') return 'bg-red-100 text-red-800';
  if (status === 'cancelled') return 'bg-gray-200 text-gray-700';
  if (status === 'rematching') return 'bg-blue-100 text-blue-800';
  return 'bg-amber-100 text-amber-800';
}
